export type ConfirmDialogIcon = 'delete' | 'warning' | 'archive' | 'restore' | 'info' | 'logout' | 'unlock';
export type ConfirmDialogTone = 'danger' | 'primary' | 'neutral' | 'success';

/** Common list-page actions that share the same confirm-dialog copy conventions. */
export type ConfirmBulkAction = 'delete' | 'archive' | 'restore';

export interface ConfirmDialogData {
  title: string;
  message: string;
  /** Optional pill badge under the message (e.g. entity name). */
  entityLabel?: string;
  icon?: ConfirmDialogIcon;
  confirmText?: string;
  cancelText?: string;
  /** Visual tone for the confirm button / icon chrome. Defaults from icon. */
  confirmTone?: ConfirmDialogTone;
  /** Optional on/off switch shown above the buttons (use with `ConfirmDialog.openWithToggle`). */
  toggle?: ConfirmDialogToggle;
}

export interface ConfirmDialogToggle {
  label: string;
  /** Helper text under the label. */
  description?: string;
  /** Initial state. Defaults to off. */
  defaultValue?: boolean;
}

/** Result of a confirm dialog with a toggle. */
export interface ConfirmDialogToggleResult {
  toggled: boolean;
}

interface ConfirmActionCopy {
  icon: ConfirmDialogIcon;
  confirmText: string;
  confirmTone: ConfirmDialogTone;
}

/** Standard delete/archive/restore copy for list pages: title is the action name, message asks "Are you sure...?" (same pattern as the Logout dialog). */
export const CONFIRM_ACTION_COPY: Record<ConfirmBulkAction, ConfirmActionCopy> = {
  delete: {
    icon: 'delete',
    confirmText: 'Delete',
    confirmTone: 'danger'
  },
  archive: {
    icon: 'archive',
    confirmText: 'Archive',
    confirmTone: 'neutral'
  },
  restore: {
    icon: 'restore',
    confirmText: 'Restore',
    confirmTone: 'success'
  }
};

/** FontAwesome class for a confirm-dialog icon. */
export function getConfirmDialogIconClass(icon: ConfirmDialogIcon): string {
  switch (icon) {
    case 'delete':
      return 'fa-solid fa-trash-can';
    case 'archive':
      return 'fa-solid fa-box-archive';
    case 'restore':
      return 'fa-solid fa-arrow-rotate-left';
    case 'info':
      return 'fa-solid fa-circle-info';
    case 'logout':
      return 'fa-solid fa-right-from-bracket';
    case 'unlock':
      return 'fa-solid fa-lock-open';
    case 'warning':
    default:
      return 'fa-solid fa-triangle-exclamation';
  }
}

/** Default visual tone for an icon when the caller doesn't set `confirmTone` explicitly. */
export function getConfirmDialogToneFromIcon(icon: ConfirmDialogIcon): ConfirmDialogTone {
  if (icon === 'delete' || icon === 'logout') return 'danger';
  if (icon === 'restore' || icon === 'unlock') return 'success';
  if (icon === 'info') return 'primary';
  if (icon === 'archive' || icon === 'warning') return 'neutral';
  return 'primary';
}
